import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, IconButton, Tooltip,
  Switch, FormControlLabel, Divider, CircularProgress, Alert,
} from '@mui/material';
import {
  Search as SearchIcon, Business as ClinicIcon, Add as AddIcon,
  Edit as EditIcon, Visibility as ViewIcon, Delete as DeleteIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BLANK_FORM = {
  account_type: 'clinic',
  name: '', address: '', phone: '', email: '', type: 'clinic',
  admin_full_name: '', organization_id: '', first_child_name: '',
  ai_unlimited: false, ai_credit_sessions: '',
};

/**
 * The four things a super admin can be creating, and how the form reads for each.
 *
 * `child` is the one level down that may optionally be named at creation — one level
 * only, because naming a clinic under a brand-new enterprise would mean inventing an
 * organisation nobody asked for to hold it. `pool` says where the AI sessions land,
 * which is the substance of this choice rather than a detail of it.
 */
const SHAPES = {
  clinic: {
    label: 'Single clinic — one practice, one location',
    nameLabel: 'Clinic name',
    adminLabel: 'the clinic administrator',
    pool: 'this clinic',
    note: 'The clinic becomes its own organisation and its main site, so its administrators can open further locations themselves later.',
  },
  organization: {
    label: 'Organisation — one customer, several locations',
    nameLabel: 'Organisation name',
    child: { key: 'first_child_name', label: 'First site name (optional)',
             help: 'Creates their first clinic and makes it the main site. Leave blank and their org admin opens it.' },
    adminLabel: 'the organisation administrator',
    pool: 'the organisation',
    note: 'Their org admin opens sites, groups them into regions, and splits the sessions between them.',
  },
  enterprise: {
    label: 'Enterprise — several organisations under one buyer',
    nameLabel: 'Enterprise name',
    child: { key: 'first_child_name', label: 'First organisation name (optional)',
             help: 'Creates their first organisation. Leave blank and their enterprise admin opens it.' },
    adminLabel: 'the enterprise administrator',
    pool: 'the enterprise',
    note: 'Their enterprise admin opens organisations and funds each one out of the pool below.',
  },
  site: {
    label: 'Another site for an existing customer',
    nameLabel: 'Site name',
    adminLabel: "this site's administrator",
    pool: 'this site',
    note: 'Added as another location of the customer you pick below.',
  },
};

const sessionsOrNull = (f) => (
  f.ai_unlimited ? null : (f.ai_credit_sessions ? parseInt(f.ai_credit_sessions, 10) : null)
);

const creditSuffix = (credit) => (
  credit
    ? ` — AI sessions: ${credit.unlimited ? 'Unlimited' : `${Number(credit.granted_sessions || 0)} sessions`}`
    : ''
);

export default function ClinicList() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [editClinic, setEditClinic] = useState(null);
  // account_type is the SHAPE of the customer being created, and it is the first thing
  // asked because it decides two things that are awkward to change afterwards: which
  // level holds the AI session pool, and what kind of administrator the customer gets.
  // 'site' is the exception — not a new customer at all, but another location for one
  // that already exists, which is what organization_id has always meant here.
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [organizations, setOrganizations] = useState([]);
  // How many clinics the platform has that are NOT shown here, because they sit under
  // a customer. Reported rather than silently omitted.
  const [groupedCount, setGroupedCount] = useState(0);
  // Delete flow: first attempt is a safe delete; a non-empty clinic escalates to force.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteForce, setDeleteForce] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const loadClinics = async () => {
    try {
      // Standalone only. A clinic that belongs to a multi-site organisation or an
      // enterprise is part of a CUSTOMER, and belongs on the Customers tree with the
      // rest of its account — in a flat list its context is invisible, and the same
      // clinic appearing in both places invites editing it from the wrong screen.
      const res = await axios.get(`${API_URL}/api/v1/superadmin/clinics`, {
        params: { scope: 'standalone' },
      });
      setClinics(res.data.clinics);
      setFiltered(res.data.clinics);
      setGroupedCount(res.data.grouped_count || 0);
    } catch { toast.error('Failed to load clinics'); }
    finally { setLoading(false); }
  };

  const loadOrganizations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/organizations`);
      setOrganizations(res.data.organizations || []);
    } catch { /* the picker just falls back to "its own organisation" */ }
  };

  useEffect(() => { loadClinics(); loadOrganizations(); }, []);

  // How many sites each organisation owns, derived from the full list rather than
  // fetched — the search filter must not change the count shown on a row.
  const siteCounts = useMemo(() => {
    const counts = {};
    clinics.forEach((c) => {
      if (c.organization_id) counts[c.organization_id] = (counts[c.organization_id] || 0) + 1;
    });
    return counts;
  }, [clinics]);

  useEffect(() => {
    if (!searchTerm) { setFiltered(clinics); return; }
    const q = searchTerm.toLowerCase();
    setFiltered(clinics.filter((c) =>
      c.name.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
    ));
  }, [searchTerm, clinics]);

  const openCreate = () => {
    setEditClinic(null);
    setForm({ ...BLANK_FORM });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditClinic(c);
    setForm({
      ...BLANK_FORM,
      account_type: 'site',
      name: c.name, address: c.address || '', phone: c.phone || '',
      email: c.email || '', type: c.type || 'clinic',
      organization_id: c.organization_id || '',
    });
    setDialogOpen(true);
  };

  // The shape being created. Editing an existing clinic is always a clinic edit, so
  // the account-type chrome is hidden there entirely.
  const shape = SHAPES[form.account_type] || SHAPES.clinic;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (editClinic) {
        const { ai_unlimited, ai_credit_sessions, ...clinicFields } = form;
        await axios.put(`${API_URL}/api/v1/superadmin/clinics/${editClinic.id}`, clinicFields);
        toast.success('Clinic updated');
      } else if (form.account_type === 'site') {
        // Another location for a customer who already exists. Unchanged path: this is
        // not onboarding, and /clinics has always done exactly this.
        const res = await axios.post(`${API_URL}/api/v1/superadmin/clinics`, {
          name: form.name, address: form.address, phone: form.phone, email: form.email,
          type: form.type,
          admin_full_name: form.admin_full_name || null,
          organization_id: form.organization_id || null,
          ai_unlimited: !!form.ai_unlimited,
          ai_credit_sessions: sessionsOrNull(form),
        });
        toast.success(`Site created${creditSuffix(res.data?.ai_credit)}`);
        // It belongs to a customer, so it will not appear on this screen. Saying so
        // beats leaving the operator to wonder why the row never showed up.
        toast(`${form.name} is managed on the Customers page, with the rest of that account.`,
              { icon: 'ℹ️', duration: 6000 });
      } else {
        // A new customer, in the shape chosen above. One call sets up the whole chain,
        // seeds the pool at the top and invites the administrator of that level.
        const res = await axios.post(`${API_URL}/api/v1/superadmin/onboard`, {
          account_type: form.account_type,
          name: form.name, address: form.address, phone: form.phone, email: form.email,
          type: form.type,
          admin_email: form.email || null,
          admin_full_name: form.admin_full_name || null,
          first_child_name: form.first_child_name || null,
          ai_unlimited: !!form.ai_unlimited,
          ai_credit_sessions: sessionsOrNull(form),
        });
        const made = (res.data?.created || []).map((n) => n.name).join(' → ');
        toast.success(`Created ${made || form.name}${creditSuffix(res.data?.ai_credit)}`);
        if (form.account_type !== 'clinic') {
          toast(`${form.name} is a customer account — manage it on the Customers page.`,
                { icon: 'ℹ️', duration: 6000 });
        }
        // An enterprise or an empty organisation owns no clinic yet, so it will not
        // appear in the list below — say where it went rather than leaving the screen
        // looking as though nothing happened.
        if (form.account_type !== 'clinic'
            && !(res.data?.created || []).some((n) => n.scope === 'clinic')) {
          toast(`${form.name} has no site yet — its administrator opens the first one.`,
                { icon: 'ℹ️' });
        }
      }
      setDialogOpen(false);
      loadClinics();
      loadOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    if (togglingId) return;
    setTogglingId(c.id);
    try {
      await axios.patch(`${API_URL}/api/v1/superadmin/clinics/${c.id}/status`);
      toast.success(`Clinic ${c.is_active ? 'deactivated' : 'activated'}`);
      loadClinics();
    } catch { toast.error('Failed to update status'); }
    finally { setTogglingId(null); }
  };

  const openDelete = (c) => { setDeleteTarget(c); setDeleteForce(false); setDeleteMsg(''); };
  const closeDelete = () => {
    if (deleteBusy) return;
    setDeleteTarget(null); setDeleteForce(false); setDeleteMsg('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await axios.delete(`${API_URL}/api/v1/superadmin/clinics/${deleteTarget.id}`, {
        params: { force: deleteForce },
      });
      toast.success(`Clinic "${deleteTarget.name}" deleted`);
      setDeleteTarget(null); setDeleteForce(false); setDeleteMsg('');
      loadClinics();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Delete failed';
      // 409 = clinic still has users/patients. Escalate to an explicit force confirmation.
      if (status === 409 && !deleteForce) {
        setDeleteForce(true);
        setDeleteMsg(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><ClinicIcon /></Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>Standalone clinics</Typography>
            <Typography variant="body2" color="text.secondary">
              Standalone practices — one clinic, no organisation or enterprise above it.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New customer</Button>
      </Box>

      {/* Say where the rest are. Showing fewer rows than the platform has, with no
          explanation, reads as missing data — which is exactly how this screen looked
          before the Customers tree existed. */}
      {groupedCount > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/customers')}>
              View customers
            </Button>
          )}
        >
          {groupedCount} more clinic{groupedCount === 1 ? '' : 's'} belong to a
          multi-site organisation or an enterprise. They are managed on the{' '}
          <strong>Customers</strong> page, alongside the rest of their account.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField fullWidth placeholder="Search by name or email…" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
          />
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Providers</TableCell>
              <TableCell align="center">Patients</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6}>Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6}>
                {searchTerm ? 'No standalone clinics match that search.'
                            : 'No standalone clinics yet. Every clinic on the platform belongs to a customer.'}
              </TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>
                  {/* No Organisation column here: every row on this screen is a
                      standalone practice, whose organisation is a wrapper carrying the
                      same name. It repeated the clinic's name and said nothing. A
                      clinic that HAS a real organisation is on the Customers tree. */}
                  <Typography fontWeight={600}>{c.name}</Typography>
                </TableCell>
                <TableCell><Chip size="small" label={c.type || 'clinic'} /></TableCell>
                <TableCell align="center">{c.provider_count ?? 0}</TableCell>
                <TableCell align="center">{c.patient_count ?? 0}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={togglingId === c.id ? '…' : (c.is_active ? 'Active' : 'Inactive')}
                    color={c.is_active ? 'success' : 'default'}
                    onClick={togglingId ? undefined : () => toggleStatus(c)}
                    sx={{ cursor: togglingId ? 'default' : 'pointer', opacity: togglingId === c.id ? 0.6 : 1 }} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => navigate(`/clinics/${c.id}`)}><ViewIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(c)}><EditIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => openDelete(c)}><DeleteIcon /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => { if (!saving) setDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editClinic ? 'Edit Clinic' : 'New customer'}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">Fields marked * are required.</Typography>

          {/* Asked first, because it decides where the sessions and the authority sit.
              Nothing here is irreversible — a single clinic that grows can be moved
              under an organisation later, and an organisation under an enterprise. */}
          {!editClinic && (
            <TextField fullWidth select label="Account type" value={form.account_type}
              onChange={(e) => setForm({
                ...form, account_type: e.target.value,
                // A shape change makes the other two fields meaningless, so they are
                // cleared rather than carried into a form where they are not shown.
                organization_id: '', first_child_name: '',
              })}
              margin="normal" required helperText={shape.note}>
              {Object.entries(SHAPES).map(([key, v]) => (
                <MenuItem key={key} value={key}>{v.label}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField fullWidth label={editClinic ? 'Name' : shape.nameLabel} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            margin="normal" required />

          {/* Clinic or hospital only means something where a clinic is involved. An
              enterprise has no such distinction, and the organisation it creates takes
              its wording from the sites added to it later. */}
          {form.account_type !== 'enterprise' && (
            <TextField fullWidth select label="Type" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })} margin="normal">
              <MenuItem value="clinic">Clinic</MenuItem>
              <MenuItem value="hospital">Hospital</MenuItem>
            </TextField>
          )}

          {/* The optional one level down, so a new customer does not start empty. */}
          {!editClinic && shape.child && (
            <TextField fullWidth label={shape.child.label} value={form.first_child_name}
              onChange={(e) => setForm({ ...form, first_child_name: e.target.value })}
              margin="normal" helperText={shape.child.help} />
          )}

          {!editClinic && form.account_type === 'site' && (
            <TextField fullWidth select label="Customer" value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
              margin="normal" required
              helperText="Which existing customer this location belongs to.">
              <MenuItem value="">Choose a customer…</MenuItem>
              {organizations.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}{siteCounts[o.id] > 1 ? ` \u2014 ${siteCounts[o.id]} sites` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField fullWidth label="Address" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} margin="normal" />
          <TextField fullWidth label="Phone" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} margin="normal" />
          <TextField fullWidth label="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} margin="normal"
            helperText={editClinic ? undefined
              : `This address is invited as ${shape.adminLabel} (single-use link, valid 48h).`} />
          {!editClinic && form.email && (
            <TextField fullWidth label="Administrator name" value={form.admin_full_name}
              onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })}
              margin="normal"
              helperText="Who that email belongs to. They can change it when they set their password." />
          )}

          {!editClinic && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">AI Sessions (optional)</Typography>
              <Typography variant="caption" color="text.secondary">
                Granted to <strong>{shape.pool}</strong>. Skip and set them later on the AI
                Credits page.
              </Typography>
              <FormControlLabel sx={{ mt: 1, display: 'block' }}
                control={<Switch checked={form.ai_unlimited}
                  onChange={(e) => setForm({ ...form, ai_unlimited: e.target.checked })} />}
                label="Unlimited AI sessions" />
              {!form.ai_unlimited && (
                <TextField fullWidth type="number" label="Initial AI sessions"
                  value={form.ai_credit_sessions}
                  onChange={(e) => setForm({ ...form, ai_credit_sessions: e.target.value })}
                  margin="normal" inputProps={{ min: 0, step: '1' }}
                  helperText="Optional — number of AI note sessions to grant up front." />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name
              || (!editClinic && form.account_type === 'site' && !form.organization_id)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? (editClinic ? 'Saving…' : 'Creating…') : (editClinic ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={closeDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          {deleteForce ? `Close ${deleteTarget?.name}?` : `Delete ${deleteTarget?.name}?`}
        </DialogTitle>
        <DialogContent>
          {!deleteForce ? (
            <Typography variant="body2">
              This permanently deletes <strong>{deleteTarget?.name}</strong>. An empty clinic
              (no providers, staff, admins or patients) is removed immediately. This cannot be undone.
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>{deleteMsg}</Typography>
              <Typography variant="body2" color="error" fontWeight={600}>
                Closing ends the whole customer account. This clinic and every other site in
                its organisation are switched off, and nobody who worked there can sign in
                again. Their protocols, AI session pools, queues and settings are deleted.
              </Typography>
              {/* The reassurance is the point of the feature, so it is stated as plainly as
                  the warning above it — and it is what the backend actually guarantees. */}
              <Typography variant="body2" sx={{ mt: 1 }} fontWeight={600}>
                No patient data is deleted. Every chart, visit, note, summary and recording is
                kept, still attached to the clinic and still naming the clinician who wrote it.
                Patients keep their logins and can still see their history.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                Staff accounts are deactivated rather than removed, because a clinical record
                whose author was erased is a worse record. Anyone who also works at a clinic
                outside this organisation keeps full access there.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDelete} disabled={deleteBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleteBusy}>
            {deleteForce ? 'Close the account' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
